import {
    getRouteBuySubscription,
    getRouteCommunity,
    getRouteCommunityChats,
    getRouteCommunityClub,
    getRouteCommunityCreate,
    getRouteCommunityPeople,
    getRouteLectures,
    getRouteMyCoursesNew,
    getRouteMyGifts,
    getRouteMyRevive,
    getRoutePackages,
    getRouteProducts,
    getRouteReviews,
    getRouteSpecialistChat,
    getRouteTestingWorkout,
    getRouteWorkoutsCalendar,
} from "@/shared/config/router";
import { hiddenWorkoutPageTarget } from "@/features/accountNavV5/model/siteWorkoutPages";
import { pointsTextLang, type PointsActionKey, type PointsTextLang } from "./pointsActions";

/**
 * «Что сделать» в окне «за что эти баллы» (владелец 03.10: «на каждую строчку за которые
 * даются баллы должна быть возможность нажать и получить нужные ссылки если речь о
 * продуктах и краткое пояснение что нужно делать»): одна-две строки — что сделать, и
 * ссылки туда, где это делают. По действию (и уточнению ctx), поэтому одинаково везде, где
 * есть строка с этим действием: на экране «Как получить баллы», на странице курса, в лиге.
 *
 * Тексты — по правилу владельца: коротко, «сделать → получить», без технических слов и
 * «если … то». Ссылки — только на страницы действий и продуктов; товары для здоровья и
 * аюрведические продукты скрыты владельцем — на них ссылок нет (тест следит).
 */

/** Куда ведёт ссылка окна. */
export type PointsGoKey =
    | "invite"
    | "courses"
    | "myCourses"
    | "subscription"
    | "tokens"
    | "diagnostics"
    | "packages"
    | "products"
    | "gifts"
    | "reviews"
    | "lectures"
    | "testWorkout"
    | "calendar"
    | "planCreate"
    | "meetings"
    | "createMeeting"
    | "goodDeeds"
    | "people"
    | "club"
    | "chats"
    | "search"
    | "specialist"
    | "app"
    | "appCard";

/** Адрес ссылки на языке страницы (на /de/ — /de/…). Пункты меню — теми же адресами, что меню кабинета. */
export const POINTS_GO_HREF: Record<PointsGoKey, (lang: string) => string> = {
    invite: (l) => `/${l}/account/affiliate`,
    courses: getRouteMyCoursesNew,
    myCourses: getRouteMyCoursesNew,
    subscription: getRouteBuySubscription,
    tokens: (l) => `/${l}/account/questions?buy=1`,
    diagnostics: (l) => `/${l}/account/diet?tab=diagnostics`,
    packages: getRoutePackages,
    products: getRouteProducts,
    gifts: getRouteMyGifts,
    reviews: getRouteReviews,
    lectures: getRouteLectures,
    testWorkout: getRouteTestingWorkout,
    calendar: (l) => getRouteWorkoutsCalendar(l),
    planCreate: (l) => `/${l}/account/plan-create`,
    meetings: getRouteCommunity,
    createMeeting: (l) => getRouteCommunityCreate(l),
    goodDeeds: (l) => `/${l}/account/good-deeds`,
    people: getRouteCommunityPeople,
    club: getRouteCommunityClub,
    chats: getRouteCommunityChats,
    search: (l) => `/${l}/account/questions`,
    specialist: getRouteSpecialistChat,
    app: getRouteMyRevive,
    // Карточка приложения в кабинете — туда же открываются скрытые календарь и план.
    appCard: (l) => hiddenWorkoutPageTarget(l),
};

/**
 * Календарь, план и тестовая тренировка на сайте скрыты (рубильник
 * SITE_WORKOUT_PAGES_HIDDEN, владелец 03.10: «пусть люди скачивают приложение»;
 * тестовая — 07.10): кому скрыты, тому окно ведёт не на них, а на карточку
 * приложения — туда, куда открылись бы и они сами.
 */
const WORKOUT_PAGE_GO: ReadonlySet<PointsGoKey> = new Set<PointsGoKey>([
    "calendar",
    "planCreate",
    "testWorkout",
]);

/** Значение по уточнению: у покупки — part | course | app | …, остальное — generic. */
type ByCtx<T> = T | ({ generic: T } & Record<string, T>);

const isByCtx = <T,>(v: ByCtx<T>): v is { generic: T } & Record<string, T> =>
    !!v && typeof v === "object" && !Array.isArray(v);

const pick = <T,>(v: ByCtx<T>, ctx: string | null | undefined): T => (isByCtx(v) ? (ctx && v[ctx]) || v.generic : v);

/** Куда ведёт каждое действие. */
export const POINTS_ACTION_GO: Record<PointsActionKey, ByCtx<PointsGoKey[]>> = {
    "ref.purchase": ["invite"],
    "ref.giftClaim": ["courses", "gifts"],
    "ref.install": ["invite"],
    "ref.started": ["invite"],
    "ref.newAtMeeting": ["meetings"],
    "contrib.purchase": {
        generic: ["products"],
        // «Эта часть» — чип стоит у самой части: на её странице покупки, рядом с её «Подробнее»
        // или в каталоге курсов; ссылка в каталог увела бы от покупки.
        part: [],
        anyPart: ["courses"],
        course: ["courses"],
        app: ["subscription"],
        tokens: ["tokens"],
        diagnostic: ["diagnostics"],
        bundle: ["packages"],
    },
    "contrib.gift": ["courses"],
    "contrib.review": ["reviews"],
    "contrib.correction": ["lectures"],
    // кнопка «Нашли ошибку?» — на любой странице; ссылка — туда, где больше всего текста
    "contrib.errorReport": ["lectures"],
    "contrib.testWorkout": ["testWorkout"],
    "work.weekOnPlan": ["calendar"],
    "work.firstPlan": ["planCreate"],
    "work.newbie": ["calendar"],
    "social.meeting": ["meetings"],
    "social.organizer": ["createMeeting"],
    "social.goodDeed": ["goodDeeds"],
    "social.addFriend": ["people"],
    "social.joinClub": ["club"],
    "social.firstMessage": ["chats"],
    "learn.lesson": ["myCourses"],
    "learn.lessonMinute": ["myCourses"],
    "learn.course": ["myCourses"],
    "learn.miniTest": ["myCourses"],
    "learn.lessonQuestion": ["myCourses"],
    "learn.sup": ["search"],
    "learn.minute": ["lectures"],
    "learn.specialist": ["specialist"],
    "learn.appLearning": ["app"],
};

type TodoTexts = {
    /** «Что сделать» — подзаголовок блока. */
    title: string;
    /** Подписи ссылок. */
    go: Record<PointsGoKey, string>;
    todo: Record<PointsActionKey, ByCtx<string>>;
};

const RU: TodoTexts = {
    title: "Что сделать",
    go: {
        invite: "Пригласить друга",
        courses: "Курсы",
        myCourses: "Мои курсы",
        subscription: "Подписка",
        tokens: "Пополнить токены",
        diagnostics: "Диагностика",
        packages: "Пакеты курсов",
        products: "Все продукты",
        gifts: "Мои подарки",
        reviews: "Написать отзыв",
        lectures: "Лекции",
        testWorkout: "Тестовая тренировка",
        calendar: "Календарь тренировок",
        planCreate: "Составить план",
        meetings: "Встречи",
        createMeeting: "Создать встречу",
        goodDeeds: "Добрые дела",
        people: "Люди",
        club: "Клуб",
        chats: "Чаты",
        search: "Умный поиск",
        specialist: "Чат со специалистом",
        app: "Приложение MY REVIVE",
        appCard: "Приложение MY REVIVE",
    },
    todo: {
        "ref.purchase": {
            generic: "Отправьте другу ссылку на часть курса.",
            part: "Отправьте другу ссылку на эту часть.",
            course: "Отправьте другу ссылку на этот курс.",
            app: "Отправьте другу ссылку на приложение MY REVIVE.",
            bundle: "Отправьте другу ссылку на пакет курсов.",
        },
        "ref.giftClaim": "Подарите другу курс и отправьте ему ссылку на подарок.",
        "ref.install": "Отправьте другу свою ссылку на приложение MY REVIVE.",
        "ref.started": "Пригласите друга по своей ссылке и помогите ему начать тренироваться.",
        "ref.newAtMeeting": "Позовите на встречу того, кто ещё не был, и сфотографируйтесь вместе.",
        "contrib.purchase": {
            generic: "Выберите продукт и оплатите его.",
            part: "Купите эту часть курса.",
            anyPart: "Выберите курс и купите его часть.",
            course: "Выберите курс и купите его.",
            app: "Купите год приложения MY REVIVE.",
            tokens: "Пополните токены умного поиска.",
            diagnostic: "Выберите диагностику и оплатите её.",
            bundle: "Выберите пакет курсов и оплатите его.",
        },
        "contrib.gift": "Выберите курс и купите его в подарок.",
        "contrib.review": "Напишите пару слов о купленном продукте.",
        "contrib.correction": "Нажмите на строку субтитров с ошибкой и предложите правку.",
        "contrib.errorReport": "Нашли ошибку на странице или в рецепте — нажмите «Нашли ошибку?» и опишите её.",
        "contrib.testWorkout": "Внесите тестовую тренировку: время и два снимка.",
        "work.weekOnPlan": "Тренируйтесь по своему плану в приложении MY REVIVE.",
        "work.firstPlan": "Составьте свой первый план тренировок.",
        "work.newbie": "Выберите полный план и проходите его целиком каждую неделю.",
        "social.meeting": "Придите на встречу и попадите на её общее фото.",
        "social.organizer": "Создайте встречу и позовите людей.",
        "social.goodDeed": "Сделайте доброе дело и отметьте его.",
        "social.addFriend": "Найдите человека и добавьте его в друзья.",
        "social.joinClub": "Откройте клуб и вступите в него.",
        "social.firstMessage": "Напишите в любой чат.",
        "learn.lesson": "Откройте курс и пройдите урок.",
        "learn.lessonMinute": "Откройте курс и пройдите урок.",
        "learn.course": "Проходите уроки и мини-тесты курса.",
        "learn.miniTest": "Пройдите мини-тест в конце главы.",
        "learn.lessonQuestion": "Задайте вопрос под уроком курса.",
        "learn.sup": "Выберите подходящий вопрос или найдите ответ в умном поиске.",
        "learn.minute": "Смотрите лекции и клипы.",
        "learn.specialist": "Напишите специалисту в чат.",
        "learn.appLearning": "Откройте гид или самодиагностику в приложении MY REVIVE.",
    },
};

const EN: TodoTexts = {
    title: "What to do",
    go: {
        invite: "Invite a friend",
        courses: "Courses",
        myCourses: "My courses",
        subscription: "Subscription",
        tokens: "Top up tokens",
        diagnostics: "Diagnostics",
        packages: "Course packages",
        products: "All products",
        gifts: "My gifts",
        reviews: "Write a review",
        lectures: "Lectures",
        testWorkout: "Test workout",
        calendar: "Workout calendar",
        planCreate: "Make a plan",
        meetings: "Meetings",
        createMeeting: "Create a meeting",
        goodDeeds: "Good deeds",
        people: "People",
        club: "Club",
        chats: "Chats",
        search: "Smart search",
        specialist: "Specialist chat",
        app: "MY REVIVE app",
        appCard: "MY REVIVE app",
    },
    todo: {
        "ref.purchase": {
            generic: "Send a friend your link to a course part.",
            part: "Send a friend your link to this part.",
            course: "Send a friend your link to this course.",
            app: "Send a friend your link to the MY REVIVE app.",
            bundle: "Send a friend your link to a course package.",
        },
        "ref.giftClaim": "Give a friend a course and send them the gift link.",
        "ref.install": "Send a friend your link to the MY REVIVE app.",
        "ref.started": "Invite a friend with your link and help them start working out.",
        "ref.newAtMeeting": "Bring someone new to a meeting and take the photo together.",
        "contrib.purchase": {
            generic: "Choose a product and pay for it.",
            part: "Buy this course part.",
            anyPart: "Choose a course and buy a part of it.",
            course: "Choose a course and buy it.",
            app: "Buy one year of the MY REVIVE app.",
            tokens: "Top up your smart search tokens.",
            diagnostic: "Choose a diagnostic and pay for it.",
            bundle: "Choose a course package and pay for it.",
        },
        "contrib.gift": "Choose a course and buy it as a gift.",
        "contrib.review": "Write a few words about a product you bought.",
        "contrib.correction": "Tap a subtitle line with a mistake and suggest a fix.",
        "contrib.errorReport": "Found a mistake on a page or in a recipe? Tap «Found an error?» and describe it.",
        "contrib.testWorkout": "Add a test workout: the times and two photos.",
        "work.weekOnPlan": "Work out on your plan in the MY REVIVE app.",
        "work.firstPlan": "Make your first workout plan.",
        "work.newbie": "Choose the full plan and do all of it every week.",
        "social.meeting": "Come to a meeting and get into its group photo.",
        "social.organizer": "Create a meeting and invite people.",
        "social.goodDeed": "Do a good deed and mark it.",
        "social.addFriend": "Find someone and add them as a friend.",
        "social.joinClub": "Open the club and join it.",
        "social.firstMessage": "Write in any chat.",
        "learn.lesson": "Open a course and finish a lesson.",
        "learn.lessonMinute": "Open a course and finish a lesson.",
        "learn.course": "Go through the course's lessons and mini-tests.",
        "learn.miniTest": "Take the mini-test at the end of a chapter.",
        "learn.lessonQuestion": "Ask a question under a course lesson.",
        "learn.sup": "Pick a matching question or find the answer in the smart search.",
        "learn.minute": "Watch lectures and clips.",
        "learn.specialist": "Write to a specialist in the chat.",
        "learn.appLearning": "Open a guide or self-diagnostics in the MY REVIVE app.",
    },
};

const DE: TodoTexts = {
    title: "Was zu tun ist",
    go: {
        invite: "Freund einladen",
        courses: "Kurse",
        myCourses: "Meine Kurse",
        subscription: "Abo",
        tokens: "Tokens aufladen",
        diagnostics: "Diagnostik",
        packages: "Kurspakete",
        products: "Alle Produkte",
        gifts: "Meine Geschenke",
        reviews: "Bewertung schreiben",
        lectures: "Vorträge",
        testWorkout: "Testtraining",
        calendar: "Trainingskalender",
        planCreate: "Plan erstellen",
        meetings: "Treffen",
        createMeeting: "Treffen erstellen",
        goodDeeds: "Gute Taten",
        people: "Menschen",
        club: "Club",
        chats: "Chats",
        search: "Intelligente Suche",
        specialist: "Chat mit Spezialisten",
        app: "App MY REVIVE",
        appCard: "App MY REVIVE",
    },
    todo: {
        "ref.purchase": {
            generic: "Senden Sie einem Freund Ihren Link zu einem Kursteil.",
            part: "Senden Sie einem Freund Ihren Link zu diesem Teil.",
            course: "Senden Sie einem Freund Ihren Link zu diesem Kurs.",
            app: "Senden Sie einem Freund Ihren Link zur App MY REVIVE.",
            bundle: "Senden Sie einem Freund Ihren Link zu einem Kurspaket.",
        },
        "ref.giftClaim": "Schenken Sie einem Freund einen Kurs und senden Sie ihm den Geschenk-Link.",
        "ref.install": "Senden Sie einem Freund Ihren Link zur App MY REVIVE.",
        "ref.started": "Laden Sie einen Freund mit Ihrem Link ein und helfen Sie ihm, mit dem Training zu beginnen.",
        "ref.newAtMeeting": "Bringen Sie jemand Neuen zu einem Treffen mit und machen Sie das Foto gemeinsam.",
        "contrib.purchase": {
            generic: "Wählen Sie ein Produkt und bezahlen Sie es.",
            part: "Kaufen Sie diesen Kursteil.",
            anyPart: "Wählen Sie einen Kurs und kaufen Sie einen Teil davon.",
            course: "Wählen Sie einen Kurs und kaufen Sie ihn.",
            app: "Kaufen Sie ein Jahr der App MY REVIVE.",
            tokens: "Laden Sie Ihre Tokens für die intelligente Suche auf.",
            diagnostic: "Wählen Sie eine Diagnostik und bezahlen Sie sie.",
            bundle: "Wählen Sie ein Kurspaket und bezahlen Sie es.",
        },
        "contrib.gift": "Wählen Sie einen Kurs und kaufen Sie ihn als Geschenk.",
        "contrib.review": "Schreiben Sie ein paar Worte zu einem gekauften Produkt.",
        "contrib.correction": "Tippen Sie auf eine Untertitelzeile mit Fehler und schlagen Sie eine Korrektur vor.",
        "contrib.errorReport": "Fehler auf einer Seite oder im Rezept? Tippen Sie auf „Fehler gefunden?“ und beschreiben Sie ihn.",
        "contrib.testWorkout": "Tragen Sie ein Testtraining ein: die Zeiten und zwei Fotos.",
        "work.weekOnPlan": "Trainieren Sie nach Ihrem Plan in der App MY REVIVE.",
        "work.firstPlan": "Erstellen Sie Ihren ersten Trainingsplan.",
        "work.newbie": "Wählen Sie den vollen Plan und erfüllen Sie ihn jede Woche ganz.",
        "social.meeting": "Kommen Sie zu einem Treffen und seien Sie auf dem gemeinsamen Foto.",
        "social.organizer": "Erstellen Sie ein Treffen und laden Sie Menschen ein.",
        "social.goodDeed": "Tun Sie eine gute Tat und markieren Sie sie.",
        "social.addFriend": "Finden Sie jemanden und fügen Sie ihn als Freund hinzu.",
        "social.joinClub": "Öffnen Sie den Club und treten Sie bei.",
        "social.firstMessage": "Schreiben Sie in einen Chat.",
        "learn.lesson": "Öffnen Sie einen Kurs und schließen Sie eine Lektion ab.",
        "learn.lessonMinute": "Öffnen Sie einen Kurs und schließen Sie eine Lektion ab.",
        "learn.course": "Gehen Sie die Lektionen und Mini-Tests des Kurses durch.",
        "learn.miniTest": "Machen Sie den Mini-Test am Ende eines Kapitels.",
        "learn.lessonQuestion": "Stellen Sie unter einer Kurslektion eine Frage.",
        "learn.sup": "Wählen Sie eine passende Frage oder finden Sie die Antwort in der intelligenten Suche.",
        "learn.minute": "Sehen Sie Vorträge und Clips.",
        "learn.specialist": "Schreiben Sie einem Spezialisten im Chat.",
        "learn.appLearning": "Öffnen Sie einen Leitfaden oder eine Selbstdiagnose in der App MY REVIVE.",
    },
};

const ES: TodoTexts = {
    title: "Qué hacer",
    go: {
        invite: "Invitar a un amigo",
        courses: "Cursos",
        myCourses: "Mis cursos",
        subscription: "Suscripción",
        tokens: "Recargar tokens",
        diagnostics: "Diagnóstico",
        packages: "Paquetes de cursos",
        products: "Todos los productos",
        gifts: "Mis regalos",
        reviews: "Escribir una reseña",
        lectures: "Conferencias",
        testWorkout: "Entrenamiento de prueba",
        calendar: "Calendario de entrenamientos",
        planCreate: "Crear un plan",
        meetings: "Encuentros",
        createMeeting: "Crear un encuentro",
        goodDeeds: "Buenas acciones",
        people: "Personas",
        club: "Club",
        chats: "Chats",
        search: "Búsqueda inteligente",
        specialist: "Chat con un especialista",
        app: "App MY REVIVE",
        appCard: "App MY REVIVE",
    },
    todo: {
        "ref.purchase": {
            generic: "Envía a un amigo tu enlace a una parte del curso.",
            part: "Envía a un amigo tu enlace a esta parte.",
            course: "Envía a un amigo tu enlace a este curso.",
            app: "Envía a un amigo tu enlace a la app MY REVIVE.",
            bundle: "Envía a un amigo tu enlace a un paquete de cursos.",
        },
        "ref.giftClaim": "Regala un curso a un amigo y envíale el enlace del regalo.",
        "ref.install": "Envía a un amigo tu enlace a la app MY REVIVE.",
        "ref.started": "Invita a un amigo con tu enlace y ayúdale a empezar a entrenar.",
        "ref.newAtMeeting": "Lleva a alguien nuevo a un encuentro y sal con él en la foto.",
        "contrib.purchase": {
            generic: "Elige un producto y págalo.",
            part: "Compra esta parte del curso.",
            anyPart: "Elige un curso y compra una parte.",
            course: "Elige un curso y cómpralo.",
            app: "Compra un año de la app MY REVIVE.",
            tokens: "Recarga tus tokens de la búsqueda inteligente.",
            diagnostic: "Elige un diagnóstico y págalo.",
            bundle: "Elige un paquete de cursos y págalo.",
        },
        "contrib.gift": "Elige un curso y cómpralo como regalo.",
        "contrib.review": "Escribe unas palabras sobre un producto que compraste.",
        "contrib.correction": "Toca una línea de subtítulos con un error y propón una corrección.",
        "contrib.errorReport": "¿Un error en una página o receta? Toca «¿Encontraste un error?» y descríbelo.",
        "contrib.testWorkout": "Añade un entrenamiento de prueba: las horas y dos fotos.",
        "work.weekOnPlan": "Entrena según tu plan en la app MY REVIVE.",
        "work.firstPlan": "Crea tu primer plan de entrenamiento.",
        "work.newbie": "Elige el plan completo y cúmplelo entero cada semana.",
        "social.meeting": "Ven a un encuentro y sal en su foto de grupo.",
        "social.organizer": "Crea un encuentro e invita a la gente.",
        "social.goodDeed": "Haz una buena acción y márcala.",
        "social.addFriend": "Encuentra a alguien y añádelo como amigo.",
        "social.joinClub": "Abre el club y únete.",
        "social.firstMessage": "Escribe en cualquier chat.",
        "learn.lesson": "Abre un curso y completa una lección.",
        "learn.lessonMinute": "Abre un curso y completa una lección.",
        "learn.course": "Recorre las lecciones y los minitests del curso.",
        "learn.miniTest": "Haz el minitest al final de un capítulo.",
        "learn.lessonQuestion": "Haz una pregunta debajo de una lección del curso.",
        "learn.sup": "Elige una pregunta adecuada o encuentra la respuesta en la búsqueda inteligente.",
        "learn.minute": "Mira conferencias y clips.",
        "learn.specialist": "Escribe a un especialista en el chat.",
        "learn.appLearning": "Abre una guía o un autodiagnóstico en la app MY REVIVE.",
    },
};

const CN: TodoTexts = {
    title: "怎么做",
    go: {
        invite: "邀请朋友",
        courses: "课程",
        myCourses: "我的课程",
        subscription: "订阅",
        tokens: "充值代币",
        diagnostics: "诊断",
        packages: "课程套餐",
        products: "所有产品",
        gifts: "我的礼物",
        reviews: "写评价",
        lectures: "讲座",
        testWorkout: "测试训练",
        calendar: "训练日历",
        planCreate: "制定计划",
        meetings: "聚会",
        createMeeting: "创建聚会",
        goodDeeds: "善行",
        people: "伙伴",
        club: "俱乐部",
        chats: "聊天",
        search: "智能搜索",
        specialist: "专家咨询",
        app: "MY REVIVE 应用",
        appCard: "MY REVIVE 应用",
    },
    todo: {
        "ref.purchase": {
            generic: "把课程部分的链接发给朋友。",
            part: "把这一部分的链接发给朋友。",
            course: "把这门课程的链接发给朋友。",
            app: "把 MY REVIVE 应用的链接发给朋友。",
            bundle: "把课程套餐的链接发给朋友。",
        },
        "ref.giftClaim": "送朋友一门课程，并把礼物链接发给他。",
        "ref.install": "把 MY REVIVE 应用的链接发给朋友。",
        "ref.started": "用您的链接邀请朋友，并帮助他开始训练。",
        "ref.newAtMeeting": "带一位新朋友来参加聚会，并一起合影。",
        "contrib.purchase": {
            generic: "选择产品并付款。",
            part: "购买这一部分课程。",
            anyPart: "选择课程并购买其中一部分。",
            course: "选择课程并购买。",
            app: "购买一年 MY REVIVE 应用。",
            tokens: "充值智能搜索代币。",
            diagnostic: "选择诊断并付款。",
            bundle: "选择课程套餐并付款。",
        },
        "contrib.gift": "选择课程并作为礼物购买。",
        "contrib.review": "为已购买的产品写几句话。",
        "contrib.correction": "点击有错误的字幕行并提交修正。",
        "contrib.errorReport": "在页面或食谱中发现错误？点击“发现错误？”并描述它。",
        "contrib.testWorkout": "添加一次测试训练：时间和两张照片。",
        "work.weekOnPlan": "在 MY REVIVE 应用中按计划训练。",
        "work.firstPlan": "制定您的第一个训练计划。",
        "work.newbie": "选择完整计划，每周全部完成。",
        "social.meeting": "参加聚会并出现在合影中。",
        "social.organizer": "创建聚会并邀请大家。",
        "social.goodDeed": "做一件善行并标记完成。",
        "social.addFriend": "找到某人并加为好友。",
        "social.joinClub": "打开俱乐部并加入。",
        "social.firstMessage": "在任意聊天中发消息。",
        "learn.lesson": "打开课程并完成一节课。",
        "learn.lessonMinute": "打开课程并完成一节课。",
        "learn.course": "完成课程的课时和小测验。",
        "learn.miniTest": "完成章节末尾的小测验。",
        "learn.lessonQuestion": "在课程课时下提问。",
        "learn.sup": "选择合适的问题，或在智能搜索中找到答案。",
        "learn.minute": "观看讲座和短片。",
        "learn.specialist": "在聊天中给专家写信。",
        "learn.appLearning": "在 MY REVIVE 应用中打开指南或自我诊断。",
    },
};

const AR: TodoTexts = {
    title: "ماذا تفعل",
    go: {
        invite: "ادعُ صديقًا",
        courses: "الدورات",
        myCourses: "دوراتي",
        subscription: "الاشتراك",
        tokens: "اشحن الرموز",
        diagnostics: "التشخيص",
        packages: "باقات الدورات",
        products: "كل المنتجات",
        gifts: "هداياي",
        reviews: "اكتب مراجعة",
        lectures: "المحاضرات",
        testWorkout: "تمرين تجريبي",
        calendar: "تقويم التمارين",
        planCreate: "ضع خطة",
        meetings: "اللقاءات",
        createMeeting: "أنشئ لقاءً",
        goodDeeds: "الأعمال الطيبة",
        people: "الأشخاص",
        club: "النادي",
        chats: "المحادثات",
        search: "البحث الذكي",
        specialist: "محادثة مع مختص",
        app: "تطبيق MY REVIVE",
        appCard: "تطبيق MY REVIVE",
    },
    todo: {
        "ref.purchase": {
            generic: "أرسل إلى صديق رابطك إلى جزء من دورة.",
            part: "أرسل إلى صديق رابطك إلى هذا الجزء.",
            course: "أرسل إلى صديق رابطك إلى هذه الدورة.",
            app: "أرسل إلى صديق رابطك إلى تطبيق MY REVIVE.",
            bundle: "أرسل إلى صديق رابطك إلى باقة دورات.",
        },
        "ref.giftClaim": "أهدِ صديقًا دورة وأرسل إليه رابط الهدية.",
        "ref.install": "أرسل إلى صديق رابطك إلى تطبيق MY REVIVE.",
        "ref.started": "ادعُ صديقًا برابطك وساعده على بدء التمرّن.",
        "ref.newAtMeeting": "ادعُ إلى لقاء شخصًا لم يحضر من قبل، وكونا معًا في الصورة.",
        "contrib.purchase": {
            generic: "اختر منتجًا وادفع ثمنه.",
            part: "اشترِ هذا الجزء من الدورة.",
            anyPart: "اختر دورة واشترِ جزءًا منها.",
            course: "اختر دورة واشترِها.",
            app: "اشترِ سنة من تطبيق MY REVIVE.",
            tokens: "اشحن رموز البحث الذكي.",
            diagnostic: "اختر تشخيصًا وادفع ثمنه.",
            bundle: "اختر باقة دورات وادفع ثمنها.",
        },
        "contrib.gift": "اختر دورة واشترِها هدية.",
        "contrib.review": "اكتب بضع كلمات عن منتج اشتريته.",
        "contrib.correction": "اضغط على سطر من الترجمة النصية فيه خطأ واقترح تصحيحًا.",
        "contrib.errorReport": "وجدت خطأ في صفحة أو وصفة؟ اضغط «وجدت خطأ؟» وصِفه.",
        "contrib.testWorkout": "أضف تمرينًا تجريبيًا: الأوقات وصورتين.",
        "work.weekOnPlan": "تمرّن وفق خطتك في تطبيق MY REVIVE.",
        "work.firstPlan": "ضع خطة تمارينك الأولى.",
        "work.newbie": "اختر الخطة الكاملة وأنجزها كلها كل أسبوع.",
        "social.meeting": "احضر لقاءً وكن في صورته الجماعية.",
        "social.organizer": "أنشئ لقاءً وادعُ الناس.",
        "social.goodDeed": "اعمل عملًا طيبًا وضع علامة عليه.",
        "social.addFriend": "اعثر على شخص وأضفه صديقًا.",
        "social.joinClub": "افتح النادي وانضم إليه.",
        "social.firstMessage": "اكتب في أي محادثة.",
        "learn.lesson": "افتح دورة وأنهِ درسًا.",
        "learn.lessonMinute": "افتح دورة وأنهِ درسًا.",
        "learn.course": "أنجز دروس الدورة واختباراتها القصيرة.",
        "learn.miniTest": "خُض الاختبار القصير في نهاية الفصل.",
        "learn.lessonQuestion": "اطرح سؤالًا تحت درس من الدورة.",
        "learn.sup": "اختر سؤالًا مناسبًا أو اعثر على الجواب في البحث الذكي.",
        "learn.minute": "شاهد المحاضرات والمقاطع.",
        "learn.specialist": "اكتب إلى مختص في المحادثة.",
        "learn.appLearning": "افتح دليلًا أو تشخيصًا ذاتيًا في تطبيق MY REVIVE.",
    },
};

const BY_LANG: Record<PointsTextLang, TodoTexts> = { ru: RU, en: EN, de: DE, es: ES, cn: CN, ar: AR };

/** Тексты блока на языке окна (zh → cn; ar — при включённом арабском интерфейсе, иначе en, как остальное окно). */
export const pointsTodoTexts = (lang: string | null | undefined): TodoTexts => BY_LANG[pointsTextLang(lang)];

export type PointsTodoLink = { key: PointsGoKey; label: string; href: string };

export type PointsTodo = { title: string; text: string; links: PointsTodoLink[] };

/** Адрес без языка и без «#…»: ссылка туда, где человек уже есть, окну не нужна. */
const bare = (href: string): string => href.split("#")[0].replace(/\/+(\?|$)/, "$1").toLowerCase();

/** Ведёт ли ссылка на страницу, где человек уже есть (адрес и параметры те же). */
export const isSamePage = (href: string, here: { pathname: string; search?: string }): boolean =>
    bare(href) === bare(`${here.pathname}${here.search || ""}`);

/** Страница покупки курса (/:lang/buy/:courseId): каталог курсов оттуда — шаг назад, от оплаты. */
const isBuyCoursePage = (here: { pathname: string }): boolean => /^\/[^/]+\/buy\/[^/]/.test(here.pathname);

/** Ссылка, которую окну на этой странице давать не нужно. */
const isNeedless = (l: { key: PointsGoKey; href: string }, here: { pathname: string; search?: string }): boolean =>
    isSamePage(l.href, here) || (l.key === "courses" && isBuyCoursePage(here));

/** Ведёт ли действие на календарь или план тренировок — окну тогда нужен рубильник их скрытия. */
export const pointsTodoUsesWorkoutPages = (action: PointsActionKey, ctx: string | null | undefined): boolean =>
    pick(POINTS_ACTION_GO[action], ctx).some((key) => WORKOUT_PAGE_GO.has(key));

export type PointsTodoOptions = {
    /** Календарь и план скрыты для этого человека (useSiteWorkoutPagesHidden). */
    workoutPagesHidden?: boolean;
};

/**
 * «Что сделать» по действию: строка и ссылки — на языке страницы siteLang (тексты — языка
 * окна, адреса — /siteLang/…). here — где окно открыто: ссылку на эту же страницу не даём,
 * а со страницы покупки курса — в каталог курсов. Календарь и план скрыты — вместо них
 * карточка приложения (одна).
 */
export const pointsTodo = (
    action: PointsActionKey,
    ctx: string | null | undefined,
    siteLang: string,
    here?: { pathname: string; search?: string },
    opts: PointsTodoOptions = {},
): PointsTodo => {
    const tx = pointsTodoTexts(siteLang);
    const keys = pick(POINTS_ACTION_GO[action], ctx).map((key): PointsGoKey =>
        opts.workoutPagesHidden && WORKOUT_PAGE_GO.has(key) ? "appCard" : key,
    );
    const links = keys
        .filter((key, i) => keys.indexOf(key) === i)
        .map((key) => ({ key, label: tx.go[key], href: POINTS_GO_HREF[key](siteLang) }))
        .filter((l) => !here || !isNeedless(l, here));
    return { title: tx.title, text: pick(tx.todo[action], ctx), links };
};
